import React, { useState } from "react";
import { PostPreview } from "./PostPreview";
import { PostEditor } from "./PostEditor";
import { PostActions } from "./PostActions";
import { AutoPublishRow } from "./AutoPublishRow";
import { MusicPicker } from "./MusicPicker";
import { DmReplyKit } from "./DmReplyKit";

const SlideList = ({ slides = [] }) => (
  <ol className="grid gap-1.5">
    {slides.map((s, i) => (
      <li key={i} className="flex gap-3 text-sm">
        <span className="text-[#BEFF38] font-black w-6 shrink-0" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{String(i + 1).padStart(2, "0")}</span>
        <span className="min-w-0"><span className="text-white font-semibold">{s.title}</span> <span className="text-zinc-400 font-mono text-xs">· {s.detail}</span></span>
      </li>
    ))}
  </ol>
);

const fmtDate = (d) =>
  d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "Unscheduled";

export const PostCard = ({ post, igConnected, notify, onUpdate, onRegen, onDelete, onPublish, onReel }) => {
  const niggle = post.kind === "niggle";
  const [format, setFormat] = useState(niggle ? "niggle" : "carousel");
  const formats = niggle ? ["niggle"] : ["carousel", "story", "reel"];
  const [editing, setEditing] = useState(false);
  const posted = post.status === "posted";

  return (
    <article data-testid={`post-card-${post.id}`} className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4 lg:p-5 grid lg:grid-cols-[320px_1fr] gap-5">
      <div className="flex flex-col gap-3">
        <div className="flex rounded-full bg-zinc-900 p-1 self-start">
          {formats.map((f) => (
            <button
              key={f}
              onClick={() => setFormat(f)}
              data-testid={`post-format-${f}-${post.id}`}
              className={`px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest transition-colors ${
                format === f ? "bg-[#BEFF38] text-black" : "text-zinc-400 hover:text-white"
              }`}
            >
              {f === "niggle" ? `Story set · ${post.slide_urls?.length || 4}` : f === "carousel" ? `Carousel · ${post.slide_urls?.length || 1}` : f === "story" ? "Story" : "Reel"}
            </button>
          ))}
        </div>
        <PostPreview post={post} format={format} onReel={onReel} />
        <MusicPicker post={post} onUpdate={onUpdate} />
      </div>
      <div className="flex flex-col gap-4 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span data-testid={`post-date-${post.id}`} className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#BEFF38]">{fmtDate(post.scheduled_for)}</span>
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">· {post.theme_label}{post.content_type ? ` · ${post.content_type}` : ""}</span>
          <span
            data-testid={`post-status-${post.id}`}
            className={`ml-auto text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
              posted ? "bg-[#BEFF38] text-black" : "bg-zinc-900 text-zinc-400 border border-zinc-800"
            }`}
          >
            {posted ? "Posted" : "Draft"}
          </span>
        </div>
        {!editing && <AutoPublishRow post={post} igConnected={igConnected} onUpdate={onUpdate} />}
        {!editing && niggle && post.niggle_region && <DmReplyKit region={post.niggle_region} keyword={post.reply_keyword} scope={`post-${post.id}`} notify={notify} />}
        {editing ? (
          <PostEditor post={post} onCancel={() => setEditing(false)} onSave={async (b) => { await onUpdate(b); setEditing(false); }} />
        ) : (
          <>
            <h3 className="text-white font-black uppercase text-2xl leading-tight" style={{ fontFamily: "Barlow Condensed, sans-serif" }} data-testid={`post-hook-${post.id}`}>
              {post.hook.replace(/\*/g, "")}
            </h3>
            <SlideList slides={post.slides} />
            <details className="group" data-testid={`post-caption-toggle-${post.id}`}>
              <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 hover:text-white">Caption</summary>
              <p className="text-zinc-300 text-sm whitespace-pre-line leading-relaxed mt-2" data-testid={`post-caption-${post.id}`}>{post.caption}</p>
            </details>
            <p className="text-white text-sm font-semibold">{post.cta}</p>
            <p className="text-[#BEFF38]/80 text-xs leading-relaxed">{(post.hashtags || []).map((t) => `#${t}`).join(" ")}</p>
            {post.reel_status === "ready" && post.music_credit && (
              <p className="text-zinc-500 text-[11px] leading-snug" data-testid={`post-music-credit-${post.id}`}>{post.music_credit}</p>
            )}
          </>
        )}
        {!editing && (
          <PostActions
            post={post}
            format={format}
            igConnected={igConnected}
            notify={notify}
            onEdit={() => setEditing(true)}
            onRegen={onRegen}
            onDelete={onDelete}
            onPublish={onPublish}
            onTogglePosted={() => onUpdate({ status: posted ? "draft" : "posted" })}
            onReel={onReel}
          />
        )}
      </div>
    </article>
  );
};
